import { Button, Chip, Input, Select, Spinner } from '@sjuni/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, RefreshCw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormError } from '@/components/FormError';
import { PublishingSidebar, type PublishStatus } from '@/components/editor/PublishingSidebar';
import { TranslatableField } from '@/components/form/TranslatableField';
import { TranslationProgress } from '@/components/form/TranslationProgress';
import { apiFetch } from '@/lib/api/client';
import { adminKeys, useResourceList, useResourceRecord, useSaveResource } from '@/lib/api/resource';
import { useAuth } from '@/lib/auth/useAuth';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import { applyApiErrors } from '@/lib/forms/applyApiErrors';
import type { TranslatedValue } from '@/lib/forms/translatable';
import { valueFor } from '@/lib/forms/translatable';
import { useUnsavedChanges } from '@/lib/forms/useUnsavedChanges';

import {
  DOCUMENT_LEVELS,
  DOCUMENT_SCOPES,
  type DocumentCategoryRow,
  type DocumentInput,
  type DocumentLevel,
  type DocumentRow,
  type DocumentScope,
  formatFileSize,
} from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §5.4 — one document's metadata, and its file.
 *
 * There is no "new" state. A document is a file: the server has no way to make
 * one without bytes, so this screen only ever edits an existing row and the
 * upload dialog is the only door in.
 *
 * The file panel is the other half of §5.4 — "Replace-file action that keeps
 * the same ID and URL (so external links don't break)". That promise is the
 * reason replacing is its own control rather than a field on this form: a save
 * that could also swap the file would have no way to say it had not.
 */
interface Fields {
  title: TranslatedValue;
  description: TranslatedValue;
  category_id: number | '';
  scope: DocumentScope;
  faculty_id: number | '';
  level: DocumentLevel;
  year: string;
  document_date: string;
  is_public: boolean;
  sort_order: number;
  status: PublishStatus;
  published_at: string | null;
  pending_review: boolean;
}

export interface DocumentEditorProps {
  recordId: string;
}

export function DocumentEditor({ recordId }: DocumentEditorProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { locale } = useEditorLocale();
  const { can, hasRole } = useAuth();
  const queryClient = useQueryClient();

  const [formError, setFormError] = useState<string | null>(null);
  const [replacing, setReplacing] = useState(false);

  const replaceInput = useRef<HTMLInputElement>(null);

  const record = useResourceRecord<DocumentRow>('documents', Number(recordId));

  const categories = useResourceList<DocumentCategoryRow>('document-categories', {
    per_page: 100,
    sort: 'sort_order',
  });

  const save = useSaveResource<DocumentRow, DocumentInput>('documents');

  const mayPublish = can('documents.publish');
  const scopeLocked = hasRole('faculty-admin') || hasRole('vocational-admin');

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<Fields>({ defaultValues: empty() });

  useEffect(() => {
    if (record.data) {
      reset(toFields(record.data.data));
    }
  }, [record.data, reset]);

  const guard = useUnsavedChanges(isDirty && !isSubmitting);

  const values = watch();

  /** §5.4's replace, as its own request. */
  const replace = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();

      form.append('files[]', file);

      return apiFetch<{ data: DocumentRow }>(`/admin/documents/${recordId}/replace`, {
        method: 'POST',
        body: form,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.resource('documents') });
      setReplacing(false);
    },
    onError: (error: Error) => {
      setFormError(error.message);
      setReplacing(false);
    },
  });

  const onSubmit = handleSubmit(async (form) => {
    setFormError(null);

    try {
      const saved = await save.mutateAsync({
        id: Number(recordId),
        input: toInput(form, mayPublish),
      });

      reset(toFields(saved.data));
    } catch (error) {
      const { formError: message, placed } = applyApiErrors(error, setError, [
        'title',
        'description',
        'category_id',
        'scope',
        'faculty_id',
        'level',
        'year',
        'document_date',
        'sort_order',
        'status',
        'published_at',
      ]);

      setFormError(message ?? (placed ? null : t('errors.generic')));
    }
  });

  if (record.isPending) {
    return (
      <div className="grid min-h-96 place-items-center">
        <Spinner size="lg" label={t('common.loading')} />
      </div>
    );
  }

  const document = record.data?.data;

  return (
    <form onSubmit={onSubmit} noValidate className="mx-auto max-w-5xl">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-h2 font-extrabold text-primary">{t('documents.edit')}</h1>

          {document?.pending_review ? (
            <Chip tone="gold" className="mt-2">
              {t('editor.publishing.awaitingReview')}
            </Chip>
          ) : null}
        </div>

        <Button type="button" variant="ghost" size="sm" onClick={() => navigate('/documents')}>
          {t('common.back')}
        </Button>
      </div>

      <FormError message={formError} />

      <div className="grid gap-8 lg:grid-cols-[1fr_280px]">
        <div className="space-y-5">
          <TranslatableField
            label={t('documents.fields.title')}
            name="title"
            value={values.title}
            onChange={(next) => setValue('title', next, { shouldDirty: true })}
            error={errors.title?.message}
          />

          <TranslatableField
            label={t('documents.fields.description')}
            hint={t('documents.fields.descriptionHint')}
            name="description"
            multiline
            rows={3}
            value={values.description}
            onChange={(next) => setValue('description', next, { shouldDirty: true })}
            required={false}
          />

          {/* §5.4's file panel. Read-only except for the one control that can
              change it, which says exactly what it preserves. */}
          {document ? (
            <section className="rounded-card border border-subtle p-4">
              <h2 className="text-caption font-extrabold tracking-wide text-muted">
                {t('documents.file.title')}
              </h2>

              <dl className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-body-sm">
                <div className="flex items-center gap-2">
                  <FileText size={16} strokeWidth={1.5} aria-hidden="true" className="text-muted" />
                  <dt className="sr-only">{t('documents.file.name')}</dt>
                  <dd className="font-semibold text-primary">{document.file_name}</dd>
                </div>

                <div>
                  <dt className="text-caption text-muted">{t('documents.file.size')}</dt>
                  <dd className="text-secondary">
                    {formatFileSize(document.file_size, (key) => t(`documents.units.${key}`))}
                  </dd>
                </div>

                <div>
                  <dt className="text-caption text-muted">{t('documents.file.downloads')}</dt>
                  <dd className="text-secondary">{document.download_count.toLocaleString()}</dd>
                </div>

                <div>
                  {/* The hash prefix, which is what makes the duplicate prompt
                      checkable by eye once it has already fired. */}
                  <dt className="text-caption text-muted">{t('documents.file.fingerprint')}</dt>
                  <dd className="font-mono text-caption text-secondary">
                    {document.file_hash_short}
                  </dd>
                </div>
              </dl>

              {can('documents.update') ? (
                <div className="mt-4">
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-subtle px-4 py-2 text-body-sm font-semibold text-secondary focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-navy-600 hover:text-primary">
                    <RefreshCw size={16} strokeWidth={1.75} aria-hidden="true" />
                    {replace.isPending || replacing
                      ? t('documents.file.replacing')
                      : t('documents.file.replace')}

                    <input
                      ref={replaceInput}
                      type="file"
                      className="sr-only"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif"
                      onChange={(event) => {
                        const file = event.target.files?.[0];

                        if (file) {
                          setFormError(null);
                          setReplacing(true);
                          replace.mutate(file);
                        }
                      }}
                    />
                  </label>

                  <p className="mt-1.5 text-caption text-muted">
                    {t('documents.file.replaceHint')}
                  </p>
                </div>
              ) : null}
            </section>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label={t('documents.fields.category')}
              {...register('category_id', { valueAsNumber: true })}
              error={errors.category_id?.message}
              options={[
                { value: '', label: t('documents.fields.categoryEmpty') },
                ...(categories.data?.data ?? []).map((category) => ({
                  value: String(category.id),
                  label: valueFor(category.name, locale),
                })),
              ]}
            />

            <Select
              label={t('documents.fields.scope')}
              hint={scopeLocked ? t('documents.fields.scopeLocked') : undefined}
              {...register('scope')}
              // §3 — a Faculty Admin and a Vocational Admin each have one legal
              // answer, and the server refuses the others.
              disabled={scopeLocked}
              error={errors.scope?.message}
              options={DOCUMENT_SCOPES.map((scope) => ({
                value: scope,
                label: t(`documents.scopes.${scope}`),
              }))}
            />

            <Select
              label={t('documents.fields.level')}
              hint={t('documents.fields.levelHint')}
              {...register('level')}
              error={errors.level?.message}
              options={DOCUMENT_LEVELS.map((level) => ({
                value: level,
                label: t(`documents.levels.${level}`),
              }))}
            />

            <Input
              {...register('year')}
              type="number"
              min={1990}
              label={t('documents.fields.year')}
              hint={t('documents.fields.yearHint')}
              error={errors.year?.message}
            />

            <Input
              {...register('document_date')}
              type="date"
              label={t('documents.fields.date')}
              hint={t('documents.fields.dateHint')}
              error={errors.document_date?.message}
            />

            <Input
              {...register('sort_order', { valueAsNumber: true })}
              type="number"
              min={0}
              label={t('documents.fields.order')}
              hint={t('documents.fields.orderHint')}
            />
          </div>

          <label className="flex items-center gap-2 text-body-sm text-primary">
            <input
              type="checkbox"
              {...register('is_public')}
              className="h-4 w-4 rounded border-strong text-navy-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
            />
            {t('documents.fields.isPublic')}
          </label>
          <p className="-mt-3 text-caption text-muted">{t('documents.fields.isPublicHint')}</p>
        </div>

        <div className="space-y-4">
          <PublishingSidebar
            resource="documents"
            status={values.status}
            onStatusChange={(status) => setValue('status', status, { shouldDirty: true })}
            publishedAt={values.published_at}
            onPublishedAtChange={(next) => setValue('published_at', next, { shouldDirty: true })}
            updatedAt={document?.updated_at}
            updatedBy={document?.updated_by}
            pendingReview={document?.pending_review}
            saving={isSubmitting}
          />

          <TranslationProgress
            fields={{
              title: { label: t('documents.fields.title'), value: values.title },
              description: {
                label: t('documents.fields.description'),
                value: values.description,
              },
            }}
          />
        </div>
      </div>

      <ConfirmDialog
        open={guard.blocking}
        title={t('editor.unsaved.title')}
        body={t('editor.unsaved.body')}
        confirmLabel={t('editor.unsaved.discard')}
        reversible={false}
        onCancel={guard.cancel}
        onConfirm={guard.proceed}
      />
    </form>
  );
}

function empty(): Fields {
  return {
    title: {},
    description: {},
    category_id: '',
    scope: 'university',
    faculty_id: '',
    level: 'all',
    year: '',
    document_date: '',
    is_public: true,
    sort_order: 0,
    status: 'draft',
    published_at: null,
    pending_review: false,
  };
}

function toFields(row: DocumentRow): Fields {
  return {
    title: row.title,
    description: row.description,
    category_id: row.category_id,
    scope: row.scope,
    faculty_id: row.faculty_id ?? '',
    level: row.level,
    // Strings in the form so an emptied number input is `''` rather than the
    // `NaN` a `valueAsNumber` register produces — which the server then
    // rejects as "must be an integer".
    year: row.year === null ? '' : String(row.year),
    document_date: row.document_date ?? '',
    is_public: row.is_public,
    sort_order: row.sort_order,
    status: row.status,
    published_at: row.published_at,
    pending_review: row.pending_review,
  };
}

function toInput(form: Fields, mayPublish: boolean): DocumentInput {
  return {
    title: form.title,
    description: form.description,
    category_id: Number(form.category_id),
    scope: form.scope,
    /*
     * Only a faculty-scoped document carries one, and the server refuses the
     * pair the other way round too. There is no control for it: §3 locks the
     * scope selector for the one role whose documents have a faculty, so the
     * value the record arrived with is the value that goes back.
     */
    faculty_id: form.scope === 'faculty' ? Number(form.faculty_id) || null : null,
    level: form.level,
    year: form.year === '' ? null : Number(form.year),
    document_date: form.document_date === '' ? null : form.document_date,
    is_public: form.is_public,
    sort_order: Number(form.sort_order),
    status: form.status,
    published_at: form.published_at,
    // §3's "Submit for review", same rule as news: saving without the publish
    // permission *is* submitting, and a publisher's save carries the flag
    // through rather than answering it.
    pending_review: mayPublish ? form.pending_review : true,
  };
}
